/*
 * A small reconnecting WebSocket wrapper.
 *
 * The browser `WebSocket` constructor cannot set request headers, so the run
 * status stream authenticates on the handshake via the **subprotocol** channel:
 * we offer `["bearer", "<jwt>"]` — a `bearer` sentinel followed by the token —
 * the server's documented header-OR-subprotocol auth. The server locates the
 * JWT as the protocol after the sentinel and echoes `bearer` on accept, so the
 * browser handshake completes. The token NEVER goes on the query string (it
 * would land in proxy/access logs).
 *
 * Features: exponential backoff with jitter, a cap, automatic reconnect on
 * unclean close, manual `close()` that suppresses reconnects, and typed
 * lifecycle callbacks. Framework-agnostic; `useRunStatus` adapts it to React.
 */

export type SocketStatus =
  | "connecting"
  | "open"
  | "reconnecting"
  | "closed";

export interface ReconnectingSocketOptions {
  url: string;
  /** Bearer token offered via the WS subprotocol for handshake auth. */
  token?: string | null;
  onMessage: (data: unknown) => void;
  onStatusChange?: (status: SocketStatus) => void;
  /** Initial backoff (ms). Default 500. */
  baseDelay?: number;
  /** Max backoff (ms). Default 15000. */
  maxDelay?: number;
}

/**
 * Build the subprotocol list that carries the bearer token on the handshake.
 * Order matters: the literal `bearer` sentinel first, then the token value. The
 * backend reads the protocol after the sentinel as the JWT and echoes `bearer`
 * on accept — the "sec-websocket-protocol as auth" pattern, with exactly ONE
 * sentinel so the token's position is unambiguous.
 */
export function authSubprotocols(token?: string | null): string[] {
  if (!token) return [];
  return [`bearer`, token];
}

export class ReconnectingSocket {
  private ws: WebSocket | null = null;
  private readonly opts: Required<
    Omit<ReconnectingSocketOptions, "token" | "onStatusChange">
  > &
    Pick<ReconnectingSocketOptions, "token" | "onStatusChange">;
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;
  private status: SocketStatus = "connecting";

  constructor(options: ReconnectingSocketOptions) {
    this.opts = {
      baseDelay: 500,
      maxDelay: 15000,
      ...options,
    };
    this.connect();
  }

  private setStatus(next: SocketStatus): void {
    if (this.status === next) return;
    this.status = next;
    this.opts.onStatusChange?.(next);
  }

  private connect(): void {
    if (this.stopped) return;
    this.setStatus(this.attempt === 0 ? "connecting" : "reconnecting");

    const protocols = authSubprotocols(this.opts.token);
    let socket: WebSocket;
    try {
      socket = protocols.length
        ? new WebSocket(this.opts.url, protocols)
        : new WebSocket(this.opts.url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = socket;

    socket.onopen = () => {
      this.attempt = 0;
      this.setStatus("open");
    };

    socket.onmessage = (event) => {
      let parsed: unknown = event.data;
      if (typeof event.data === "string") {
        try {
          parsed = JSON.parse(event.data);
        } catch {
          parsed = event.data; // non-JSON frame — pass through as-is
        }
      }
      this.opts.onMessage(parsed);
    };

    socket.onclose = () => {
      this.ws = null;
      if (!this.stopped) this.scheduleReconnect();
    };

    // Let onclose drive reconnect; just surface the transitional state.
    socket.onerror = () => {
      this.setStatus(this.attempt === 0 ? "connecting" : "reconnecting");
    };
  }

  private scheduleReconnect(): void {
    if (this.stopped) return;
    this.setStatus("reconnecting");
    const exp = Math.min(
      this.opts.maxDelay,
      this.opts.baseDelay * 2 ** this.attempt,
    );
    const jitter = exp * (0.5 + Math.random() * 0.5); // 50–100% of exp
    this.attempt += 1;
    this.timer = setTimeout(() => this.connect(), jitter);
  }

  /** Send a frame if the socket is open (no-op otherwise). */
  send(data: string): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(data);
  }

  /** Permanently close and stop reconnecting. */
  close(): void {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.setStatus("closed");
    if (this.ws) {
      this.ws.onclose = null; // suppress the reconnect path
      this.ws.close();
      this.ws = null;
    }
  }
}
